import React, { useEffect, useRef, useState } from 'react';
import axiosInstance from '../../Services/axiosInstance';
import { useToaster } from '../common/Toaster';
import LoadingSpinner from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { UserApiResponse, getApiErrorDetails, isAxiosError } from '../../types/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ArrowRightLeft, Users, AlertCircle, Search, RefreshCw, Trash2, Loader2 } from 'lucide-react';

interface MigrationUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  workspaceId: string | null;
  organization: string | null;
  subscriptionId: string | null;
}

interface SubscriptionOption {
  label: string;
  value: string;
}

const isValidRedirectUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const Migration: React.FC = () => {
  const { notify } = useToaster();
  const [users, setUsers] = useState<MigrationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<MigrationUser | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<MigrationUser | null>(null);
  const [subscriptionOptions, setSubscriptionOptions] = useState<SubscriptionOption[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<string>('');
  const [isMigrating, setIsMigrating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchUsers = async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get('/api/UserMigration/users', { signal });
      if (signal?.aborted) return;
      const mappedUsers: MigrationUser[] = response.data.map((item: UserApiResponse) => ({
        id: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        workspaceId: item.workspaceId || null,
        organization: item.applicationSubscription?.organisation?.name || null,
        subscriptionId: item.applicationSubscription?.id || null,
      }));

      setUsers(mappedUsers);

      // Extract unique subscriptions
      const subs = Array.from(
        new Set(
          mappedUsers
            .filter(u => u.subscriptionId && u.organization)
            .map(u => JSON.stringify({ id: u.subscriptionId, name: u.organization }))
        )
      ).map(s => {
        const { id, name } = JSON.parse(s);
        return { value: id, label: name };
      });

      setSubscriptionOptions(subs);
      if (subs.length > 0) {
        setSelectedSubscription(subs[0].value);
      }
    } catch (err: unknown) {
      if (signal?.aborted) return;
      console.error('Error fetching migration users:', err);
      if (isAxiosError(err) && err.response?.status === 404) {
        setError('Migration service is not available. The API endpoint may not be configured.');
      } else {
        const details = getApiErrorDetails(err);
        setError(details.message);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchUsers(controller.signal);
    return () => {
      controller.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const pendingUsers = users.filter(u => !u.workspaceId);
  const usersWithSubs = users.filter(u => u.subscriptionId);

  const filteredUsers = pendingUsers.filter(u => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      u.firstName.toLowerCase().includes(q) ||
      u.lastName.toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  });

  const handleMigrate = (user: MigrationUser) => {
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const handleMigrateConfirm = async () => {
    if (!selectedUser || !selectedSubscription) {
      notify('Validation', 'Please select a subscription', 'warning');
      return;
    }

    setIsMigrating(true);

    try {
      const payload = {
        subscriptionId: selectedSubscription,
        firstName: selectedUser.firstName,
        lastName: selectedUser.lastName,
        email: selectedUser.email,
        SourceUserId: selectedUser.id,
      };

      const response = await axiosInstance.post('/api/UserMigration/CreateUserMigrationWizardUrl', payload);
      const wizardUrl = response.data?.data?.createUserMigrationWizardUrl?.userMigrationWizardUrl;

      if (wizardUrl) {
        if (!isValidRedirectUrl(wizardUrl)) {
          notify('Error', 'Invalid migration wizard URL received', 'danger');
          return;
        }
        setIsDialogOpen(false);
        notify('Success', 'Redirecting to migration wizard...', 'success');
        // External URL - window.location.href is intentional for cross-origin navigation
        window.location.href = wizardUrl;
      } else {
        notify('Error', 'Failed to get migration wizard URL', 'danger');
      }
    } catch (err: unknown) {
      const details = getApiErrorDetails(err);
      notify('Error', details.message || 'Migration failed', 'danger');
    } finally {
      setIsMigrating(false);
    }
  };

  const handleDeleteClick = (user: MigrationUser) => {
    setUserToDelete(user);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);

    try {
      await axiosInstance.delete(`/api/UserMigration/users/${userToDelete.id}`);
      notify('Success', `User ${userToDelete.firstName} ${userToDelete.lastName} deleted successfully`, 'success');
      setIsDeleteDialogOpen(false);
      setUserToDelete(null);
      // Refresh the user list with a new AbortController
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;
      fetchUsers(controller.signal);
    } catch (err: unknown) {
      const details = getApiErrorDetails(err);
      notify('Error', details.message || 'Failed to delete user', 'danger');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner label="Loading migration data..." />;
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ArrowRightLeft className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle>User Migration</CardTitle>
                <CardDescription>
                  Migrate users from external systems to Access Workspace
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{pendingUsers.length} pending</Badge>
                <Badge variant="success">{usersWithSubs.length} with subscriptions</Badge>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-full bg-destructive/10">
                  <AlertCircle className="h-5 w-5 text-destructive" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-destructive">Unable to load migration data</h3>
                  <p className="text-sm text-muted-foreground mt-1">{error}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => fetchUsers()}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Card */}
        {!error && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <CardTitle className="text-lg">Pending Migrations</CardTitle>
                  <span className="text-sm text-muted-foreground">
                    ({pendingUsers.length} user{pendingUsers.length !== 1 ? 's' : ''})
                  </span>
                </div>
                {pendingUsers.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="search" className="sr-only">Search</Label>
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="search"
                        placeholder="Search users..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-8 w-[200px]"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {pendingUsers.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="No pending migrations"
                  description="All users have been migrated or there are no users in the system yet."
                />
              ) : filteredUsers.length === 0 ? (
                <EmptyState
                  icon={Search}
                  title="No results found"
                  description={`No users match "${searchQuery}"`}
                  action={{
                    label: 'Clear search',
                    onClick: () => setSearchQuery('')
                  }}
                />
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead className="hidden md:table-cell">Organization</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            {user.firstName} {user.lastName}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            {user.organization || '—'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button size="sm" onClick={() => handleMigrate(user)} disabled={isMigrating}>
                                    {isMigrating ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <ArrowRightLeft className="h-4 w-4 mr-1" />
                                    )}
                                    Migrate
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Migrate user to Access Workspace</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => handleDeleteClick(user)}
                                    disabled={isDeleting}
                                    aria-label={`Delete ${user.firstName} ${user.lastName}`}
                                  >
                                    {isDeleting ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete user permanently</TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Migration Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Migrate User</DialogTitle>
              <DialogDescription>
                Select a subscription to migrate {selectedUser?.firstName} {selectedUser?.lastName} to Access Workspace.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-4">
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm"><strong>Name:</strong> {selectedUser?.firstName} {selectedUser?.lastName}</p>
                <p className="text-sm"><strong>Email:</strong> {selectedUser?.email}</p>
              </div>

              {subscriptionOptions.length > 0 ? (
                <div className="space-y-2">
                  <Label htmlFor="subscription">Target Subscription</Label>
                  <Select value={selectedSubscription} onValueChange={setSelectedSubscription}>
                    <SelectTrigger id="subscription">
                      <SelectValue placeholder="Select subscription" />
                    </SelectTrigger>
                    <SelectContent>
                      {subscriptionOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    No subscriptions available. Please ensure subscriptions are configured.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isMigrating}>
                Cancel
              </Button>
              <Button
                onClick={handleMigrateConfirm}
                disabled={isMigrating || !selectedSubscription}
              >
                {isMigrating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : 'Start Migration'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this user? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm"><strong>Name:</strong> {userToDelete?.firstName} {userToDelete?.lastName}</p>
                <p className="text-sm"><strong>Email:</strong> {userToDelete?.email}</p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : 'Delete User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default Migration;
